import { Injectable } from '@angular/core';
import { catchError, map, mergeMap, take, tap } from 'rxjs/operators';
import { Observable, of, throwError } from 'rxjs';
import { CurrentTransporterService } from './current-transporter.service';
import { CurrentDataService } from './current-data-service';
import { CustomerService } from './customer.service';
import { AuthServerProvider } from './auth-jwt.service';
import { ApplicationConfigService } from './application-config.service';
import { NotificationService } from './notification.service';
import { AccountService } from './account.service';
import { CustomerViewDto } from '../models/managed-user.model';
import { NotificationViewDto } from '../models/notification-view-dto';
import { Pagination } from '../models/pagination.model';
import { User } from '../models/user.model';
import { environment } from '../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class InitService {
  public resourceUrl = environment.urlApi + '/api/transporter/current';

  constructor(
    private currentDataService: CurrentDataService,
    private currentTransporterService: CurrentTransporterService,
    private customerService: CustomerService,
    private accountService: AccountService,
    private authServerProvider: AuthServerProvider,
    private applicationConfig: ApplicationConfigService,
    private notificationService: NotificationService
  ) {}

  public syncFetchAccountAndUser(): Promise<void> {
    return new Promise(resolve => {
      this.fetchAll()
        .pipe(take(1))
        .subscribe(() => resolve());
    });
  }

  public fetchAccount(): Observable<void> {
    return this.accountService.identity().pipe(
      map(account => {
        if ('anonymoususer' === account?.login) {
          throwError(() => 'Token expired !');
          this.authServerProvider.logout().subscribe({ complete: () => this.accountService.authenticate(null) });
          this.currentDataService.updateAccount(undefined);
          this.currentDataService.updateUser(undefined);
        } else {
          this.currentDataService.updateAccount(account ?? undefined);
        }
      }),
      catchError(err => {
        console.error(err);
        // en cas d'erreur on invalide le token et le compte.
        this.authServerProvider.logout().subscribe({ complete: () => this.accountService.authenticate(null) });
        this.currentDataService.updateAccount(undefined);
        this.currentDataService.updateUser(undefined);
        return of(undefined);
      })
    );
  }

  public fetchAll(): Observable<CustomerViewDto | undefined> {
    if (!this.authServerProvider.getToken()) {
      return of(undefined);
    }

    return this.fetchAccount().pipe(
      mergeMap(() => this.accountService.getCurrentUser()),
      tap((user: User) => this.currentDataService.updateUser(user)),
      // @ts-ignore
      mergeMap(() => this.customerService.findCurrentCustomer()),
      tap((customer?: any) => this.currentDataService.updateCustomer(customer)),
      tap(() => {
        this.notificationService.getCurrentNotifications().subscribe((res: { content: NotificationViewDto[]; pagination: Pagination }) => {
          this.currentDataService.updateCurrentNotifications(res);
        });
      }),
      catchError((err: unknown) => {
        console.error(err);
        return throwError(() => err);
      })
    );
  }

  async init(): Promise<any> {
    await this.applicationConfig.getConf();
    await Promise.all([this.currentTransporterService.initTransporter().then(), this.syncFetchAccountAndUser().then()]).then();
  }
}
